import {
    FC,
    ReactElement,
    useRef,
    useState,
} from 'react';

import {
    AddingCard,
    CallToActionNullState,
    Dialog,
    DialogContent,
    ExpandableTagList,
    Loading,
} from '../../../compositions';
import { closeDialog, openDialog } from '../../../helpers/dialog';
import { getRoleForTranslation, userHasRole } from '../../../helpers/role';
import { capitalizeFirstCharacter } from '../../../helpers/string';
import trans from '../../../helpers/trans';
import { SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { FormOption, UserRole } from '../../../types';
import { SkillsForm } from '../..';

import './SkillsCard.scss';

interface SkillsCardProps {
    isLoading?: boolean;
    suggestionIsLoading?: boolean;
    hasError?: boolean;
    userRoles?: UserRole[];
    isEditable: boolean;
    activeSkills: SkillBase[];
    searchedSkills: SkillBase[];
    suggestedSkills: SkillBase[];
    skillsError?: string;
    occupationOptions: FormOption[];
    onSkillSearchChange?: (query: string) => void;
    onOccupationSearchChange?: (query: string) => void;
    onOccupationSearchSelect?: (query: string) => void;
    onUpdateSkills: (skillsFormData: SkillsFormData) => void;
    onCloseSkillsDialog: () => void;
    className?: string;
}

const SkillsCard: FC<SkillsCardProps> = ({
    isLoading,
    suggestionIsLoading,
    hasError,
    userRoles = [],
    isEditable,
    activeSkills,
    searchedSkills,
    suggestedSkills,
    skillsError,
    occupationOptions,
    onSkillSearchChange,
    onOccupationSearchChange,
    onOccupationSearchSelect,
    onUpdateSkills,
    onCloseSkillsDialog,
    className = '',
}): ReactElement => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const [skillsFormKey, setSkillsFormKey] = useState<number>(0);

    const handleOpenDialog = (): void => openDialog(dialogRef);
    const handleCloseDialog = (): void => {
        closeDialog(dialogRef);
        onCloseSkillsDialog();

        setSkillsFormKey(skillsFormKey + 1);
    };

    const handleUpdateSkills = (skillsFormData: SkillsFormData): void => {
        onUpdateSkills(skillsFormData);
        handleCloseDialog();
    };

    const skillsThreshold = 10;

    return (
        <AddingCard
            isEditable={isEditable && activeSkills.length > 0}
            showFillLabel={activeSkills.length === 0 && !isLoading}
            title={trans('containers.skillsCard.title')}
            item={trans('containers.skillsCard.skill')}
            description={trans(`containers.skillsCard.description.${getRoleForTranslation(userRoles)}`)}
            userRoles={userRoles}
            onAddClick={handleOpenDialog}
            className={`skills-card ${className}`}
        >
            {isLoading && <Loading className="skills-card__loader" />}

            {!isLoading && activeSkills.length > 0 && (
                <ExpandableTagList
                    listItems={activeSkills.map(skill => capitalizeFirstCharacter(skill.label))}
                    listItemsThreshold={skillsThreshold}
                    buttonLabel={trans('containers.skillsCard.showAllSkills')}
                    expandedButtonLabel={trans('containers.skillsCard.hideAllSkills')}
                    className="skills-card__skills-list"
                />
            )}

            {!isLoading && activeSkills.length === 0 && (
                <CallToActionNullState
                    isEditable={!hasError && userHasRole(UserRole.candidate, userRoles)}
                    icon="skills-passport"
                    paragraph={trans('containers.skillsCard.nullState.paragraph')}
                    subParagraph={trans('containers.skillsCard.nullState.subParagraph')}
                    buttonText={trans('containers.skillsCard.nullState.callToAction')}
                    onButtonClick={handleOpenDialog}
                    className="skills-card__null-state"
                />
            )}

            <Dialog
                ref={dialogRef}
                onClose={handleCloseDialog}
                className="skills-card__dialog"
            >
                <DialogContent title={trans('containers.skillsCard.title')}>
                    <SkillsForm
                        key={skillsFormKey}
                        showSearch
                        showOccupationSearch
                        isLoading={suggestionIsLoading}
                        activeSkills={activeSkills}
                        searchedSkillOptions={searchedSkills}
                        occupationOptions={occupationOptions}
                        suggestedSkillOptions={suggestedSkills}
                        error={skillsError}
                        onSkillSearchChange={onSkillSearchChange}
                        onOccupationSearchChange={onOccupationSearchChange}
                        onOccupationSearchSelect={onOccupationSearchSelect}
                        onSubmit={handleUpdateSkills}
                        onCancel={handleCloseDialog}
                    />
                </DialogContent>
            </Dialog>
        </AddingCard>
    );
};

export default SkillsCard;
